import { ApmBase } from "@elastic/apm-rum";
import jwtDecode from "jwt-decode";

import ClientConfig from "shared/config";
import { userDataProps } from "shared/common/contexts/AuthProvider";

export type tokenType = {
  access_token: string;
  expires_in: number;
  id_token?: string;
  refresh_token: string;
  token_type: string;
};

export type tokenTypeWithProvider = {
  domain: string;
  clientId: string;
  access_token: string;
  expires_in: number;
  id_token?: string;
  refresh_token: string;
  token_type: string;
};

export type refreshTokenType = {
  access_token: string;
  expires_in: number;
  id_token: string;
  token_type: string;
};

type idTokenType = {
  aud: string;
  auth_time: number;
  at_hash: string;
  "cognito:groups": string[];
  "cognito:username": string;
  "custom:expirationdate": string;
  "custom:tenant": string;
  "custom:user": string;
  "custom:usergroup": string;
  email: string;
  email_verified: boolean;
  exp: number;
  family_name: string;
  given_name: string;
  iat: number;
  identities: object[];
  iss: string;
  jti: string;
  origin_jti: string;
  preferred_username: string;
  sub: string;
  token_use: string;
};

type accessTokenType = {
  auth_time: number;
  client_id: string;
  "cognito:groups": string[];
  event_id: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  origin_jti: string;
  scope: string;
  sub: string;
  token_use: string;
  username: string;
};

let tokens_json: tokenType | null = null;

const sha256 = async (str: string) => {
  try {
    return await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  } catch (error) {
    throw new Error(
      "Subtle crypto is not supported, use a secure context (HTTPS)"
    );
  }
};

const generateNonce = async () => {
  const hash = await sha256(
    crypto.getRandomValues(new Uint32Array(4)).toString()
  );
  // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
  const hashArray = Array.from(new Uint8Array(hash));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

const base64URLEncode = (string: ArrayBuffer) => {
  return btoa(
    String.fromCharCode.apply(null, Array.from(new Uint8Array(string)))
  )
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export function isLoggedin() {
  const tokens = localStorage.getItem("tokens");
  if (tokens) {
    const tokens_verify = verifyTokens(tokens);
    if (tokens_verify && tokens_verify.access_token) {
      const access_token_diff = getRemainingSessionTime(
        tokens_verify.access_token
      );
      if (access_token_diff > 0) {
        return true;
      }
    }
  }
  return false;
}

function verifyTokens(tokens: string) {
  let verify = false;
  try {
    tokens_json = JSON.parse(tokens);
    if (tokens_json) {
      jwtDecode(tokens_json.id_token || "");
      if (tokens_json.access_token) verify = true;
      if (tokens_json.refresh_token) verify = true;
    }
  } catch (err) {
    return false;
  }
  if (!verify) return false;
  return tokens_json;
}

// Function to redirect to classic Identity Provider (Shibboleth)
export async function redirectToShibbolethIDP(
  mode: "popup" | "redirect" = "redirect"
) {
  try {
    let state = await generateNonce();
    state += `-${decodeURIComponent(window.location.origin)}`.replaceAll(
      "&",
      "$"
    ); // Replace $ with & -> & is being not returned, therefore it is replaced with $
    const codeVerifier = await generateNonce();
    sessionStorage.setItem(`codeVerifier-${state}`, codeVerifier);
    const codeChallenge = base64URLEncode(await sha256(codeVerifier));

    let redirect_uri = window.location.origin;
    if (mode === "popup") {
      if (ClientConfig.stage === "prod") {
        redirect_uri = "https://app.infra3d.com";
      } else {
        redirect_uri = `https://app.${ClientConfig.stage}.infra3d.com`;
      }
    }
    redirect_uri += "/";

    const url = `${ClientConfig.shibboDomain}/oauth2/authorize?client_id=${ClientConfig.shibboClientId}&response_type=code&scope=openid&redirect_uri=${redirect_uri}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

    if (mode === "redirect") {
      localStorage.setItem(
        "cognitoInfos",
        JSON.stringify({
          domain: ClientConfig.shibboDomain,
          clientId: ClientConfig.shibboClientId,
        })
      );
      window.location.href = url;
      return {} as tokenTypeWithProvider; // This line will never be reached
    }

    const popup = window.open(url, "Login to infra3D", "width=500,height=600");

    if (!popup) {
      throw new Error("Popup blocked");
    }

    // Wait until popup.closed === false
    let maxWait = 3000; // 3 seconds max
    let interval = 50;
    let waited = 0;
    while (popup.closed && waited < maxWait) {
      await new Promise((r) => setTimeout(r, interval));
      waited += interval;
    }

    if (popup.closed) {
      throw new Error("Popup never initialized");
    }

    // Now attach listener
    const authCode = await new Promise<string>((resolve, reject) => {
      const listener = (event: MessageEvent) => {
        // Skip other messages (e.g. from plugins)
        if (!event.data || event.data.source !== "infra3d" || !event.data.code)
          return;
        if (
          event.origin.endsWith(".infra3d.com") ||
          event.origin.startsWith("http://localhost")
        ) {
          cleanup();
          resolve(event.data.code);
          window.removeEventListener("message", listener);
        } else {
          cleanup();
          reject(new Error("Origin mismatch"));
        }
      };
      const cleanup = () => {
        clearInterval(checkClose);
        window.removeEventListener("message", listener);
      };
      window.addEventListener("message", listener);
      const checkClose = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error("User cancelled login"));
        }
      }, 200);
    });

    const tokenUrl = `${ClientConfig.shibboDomain}/oauth2/token`;

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: ClientConfig.shibboClientId,
      code: authCode,
      code_verifier: codeVerifier,
      redirect_uri: redirect_uri,
    });

    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body,
    });

    const json = await response.json();
    return {
      domain: ClientConfig.shibboDomain,
      clientId: ClientConfig.shibboClientId,
      access_token: json.access_token,
      expires_in: json.expires_in,
      id_token: json.id_token,
      refresh_token: json.refresh_token,
      token_type: json.token_type,
    };
  } catch (error) {
    console.error("Error while redirecting to shibboleth IDP:", error);
    throw error;
  }
}

// Function to redirect to external Identity Provider (IDP)
export async function redirectToExternalIDP(
  domain: string,
  clientId: string,
  mode: "popup" | "redirect" = "redirect"
): Promise<tokenTypeWithProvider> {
  try {
    let state = await generateNonce();
    state += `-${decodeURIComponent(window.location.origin)}`.replaceAll(
      "&",
      "$"
    ); // Replace $ with & -> & is being not returned, therefore it is replaced with $
    const codeVerifier = await generateNonce();
    sessionStorage.setItem(`codeVerifier-${state}`, codeVerifier);
    const codeChallenge = base64URLEncode(await sha256(codeVerifier));

    let redirect_uri = window.location.origin;
    if (mode === "popup") {
      if (ClientConfig.stage === "prod") {
        redirect_uri = "https://app.infra3d.com/";
      } else {
        redirect_uri = `https://app.${ClientConfig.stage}.infra3d.com/`;
      }
    }

    // Construct the redirect URL for the IDP
    const url = `${domain}/oauth2/authorize?client_id=${clientId}&response_type=code&scope=openid&redirect_uri=${redirect_uri}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

    if (mode === "redirect") {
      localStorage.setItem(
        "cognitoInfos",
        JSON.stringify({ domain: domain, clientId: clientId })
      );
      window.location.href = url;
      return {} as tokenTypeWithProvider; // This line will never be reached
    }

    const popup = window.open(url, "Login to infra3D", "width=500,height=600");

    if (!popup) {
      throw new Error("Popup blocked");
    }

    // Wait until popup.closed === false
    let maxWait = 3000; // 3 seconds max
    let interval = 50;
    let waited = 0;
    while (popup.closed && waited < maxWait) {
      await new Promise((r) => setTimeout(r, interval));
      waited += interval;
    }

    if (popup.closed) {
      throw new Error("Popup never initialized");
    }

    // Now attach listener
    const authCode = await new Promise<string>((resolve, reject) => {
      const listener = (event: MessageEvent) => {
        // Skip other messages (e.g. from plugins)
        if (!event.data || event.data.source !== "infra3d" || !event.data.code)
          return;
        if (
          event.origin.endsWith(".infra3d.com") ||
          event.origin.startsWith("http://localhost")
        ) {
          cleanup();
          resolve(event.data.code);
        } else {
          cleanup();
          reject(new Error("Origin mismatch"));
        }
      };

      const cleanup = () => {
        clearInterval(checkClose);
        window.removeEventListener("message", listener);
      };

      window.addEventListener("message", listener);

      const checkClose = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error("User cancelled login"));
        }
      }, 200);
    });

    const tokenUrl = `${domain}/oauth2/token`;

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code: authCode,
      code_verifier: codeVerifier,
      redirect_uri: redirect_uri,
    });

    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Token exchange failed: ${errorBody}`);
    }

    const json = await response.json();

    return {
      domain,
      clientId,
      access_token: json.access_token,
      expires_in: json.expires_in,
      id_token: json.id_token,
      refresh_token: json.refresh_token,
      token_type: json.token_type,
    };
  } catch (error) {
    console.error("Error while redirecting to external IDP:", error);
    throw error;
  }
}

export function logout(redirect_uri = "", logoutURL = "") {
  if (!isLoggedin()) return;
  const cognitoInfos = localStorage.getItem("cognitoInfos");
  const cognitoInfosParsed = cognitoInfos ? JSON.parse(cognitoInfos) : {};

  if (localStorage.getItem("tokens")) {
    localStorage.removeItem("tokens");
  }
  if (localStorage.getItem("cognitoInfos")) {
    localStorage.removeItem("cognitoInfos");
  }

  let url = "";
  if (!cognitoInfosParsed.domain) {
    // Has an old login with the old process
    const cognitoLogin = localStorage.getItem("cognitoLogin");
    url = `${ClientConfig.shibboDomain}/logout?client_id=${ClientConfig.shibboClientId}&logout_uri=${logoutURL || ClientConfig.logoutURL}${redirect_uri}`;
    if (cognitoLogin) {
      localStorage.removeItem("cognitoLogin"); // As it is not used anymore
    }
  } else {
    url = `${cognitoInfosParsed.domain}/logout?client_id=${cognitoInfosParsed.clientId}&logout_uri=${logoutURL || ClientConfig.logoutURL}${redirect_uri}`;
  }
  window.location.href = url;
}

export function getUserData(tokens: tokenType): userDataProps {
  const userdata: userDataProps = {
    username: "",
    usermail: "",
    user_firstname: "",
    user_familyname: "",
    tenant: "Demo",
    usergroups: [],
    userpool_id: "",
    scope: [],
    sub: "",
    client_id: "",
    isMigrated: true,
    isAdmin: false,
  };

  if (tokens.id_token) {
    const decoded_id_token: idTokenType = jwtDecode(tokens.id_token);
    const userpool_id = decoded_id_token.iss.split("/").at(-1) ?? "";
    const customGroup = decoded_id_token["custom:usergroup"];

    userdata.username = decoded_id_token["cognito:username"];
    userdata.usermail = decoded_id_token.email;
    userdata.user_firstname = decoded_id_token.given_name;
    userdata.user_familyname = decoded_id_token.family_name;
    userdata.tenant = decoded_id_token["custom:tenant"] ?? userpool_id;
    userdata.userpool_id = userpool_id;
    userdata.usergroups = customGroup
      ? [customGroup]
      : decoded_id_token["cognito:groups"]?.length > 0
        ? decoded_id_token["cognito:groups"]
        : [];
  }

  if (tokens.access_token) {
    const decoded_access_token: accessTokenType = jwtDecode(
      tokens.access_token
    );

    // Extract user info from scopes (e.g. "user/john", "usergroup/admins", "tenant/acme")
    if (decoded_access_token.scope) {
      const scopes = decoded_access_token.scope.split(" ");

      for (const scope of scopes) {
        const [type, value] = scope.split("/");
        switch (type) {
          case "user":
            userdata.username = value;
            break;
          case "usergroup":
            userdata.usergroups = [value];
            break;
          case "tenant":
            userdata.tenant = value;
            break;
        }
      }

      userdata.scope = scopes;
      userdata.isAdmin =
        decoded_access_token.scope.includes("role/super_admin") ||
        decoded_access_token.scope.includes("role/admin");
    }

    // Fallback because M2M auth doesn't get an id_token
    if (userdata.usergroups.length === 0) {
      userdata.usergroups = decoded_access_token["cognito:groups"] ?? [];
    }

    userdata.userpool_id = decoded_access_token.iss.split("/").at(-1) ?? "";
    userdata.sub = decoded_access_token.sub;
    userdata.client_id = decoded_access_token.client_id;
  }

  userdata.isMigrated = !ClientConfig.notMigrated.some((poolId) =>
    userdata.userpool_id.includes(poolId)
  );

  return userdata;
}

// Performs user login.
export async function login(
  setUserData: (data: userDataProps) => void,
  setTokens: (tokens: tokenType) => void,
  apm: ApmBase | undefined
) {
  // Clear expired data and get parameters from URL
  const cognitoInfos = localStorage.getItem("cognitoInfos");
  const cognitoInfosParsed = cognitoInfos ? JSON.parse(cognitoInfos) : {};
  const searchParams = new URL(window.location.href).searchParams;
  let state = searchParams.get("state");
  if (state === null) return;
  const codeVerifier = sessionStorage.getItem(`codeVerifier-${state}`);
  if (codeVerifier === null) return;
  state = state.replaceAll("$", "&");

  // Request tokens from Cognito or Trial
  const res = await fetch(`${cognitoInfosParsed.domain}/oauth2/token`, {
    method: "POST",
    headers: new Headers({
      "content-type": "application/x-www-form-urlencoded",
    }),
    body: Object.entries({
      grant_type: "authorization_code",
      client_id: cognitoInfosParsed.clientId,
      code: searchParams.get("code"),
      code_verifier: codeVerifier,
      redirect_uri: `${window.location.origin}/`,
    })
      .map(([k, v]) => `${k}=${v}`)
      .join("&"),
  });
  sessionStorage.removeItem(`codeVerifier-${state}`);

  // Logout if request is not successful
  if (!res.ok) {
    logout();
  }

  // Parse tokens from response
  const tokens_json = await res.json();
  if (tokens_json === null) return;

  // Set tokens and save them to local storage
  setTokens(tokens_json);
  window.localStorage.setItem("tokens", JSON.stringify(tokens_json));

  // Set refresh timer
  setTimeout(
    () => refresh(setUserData, setTokens, apm),
    tokens_json.expires_in * 1000 - 60000
  );

  // Redirect user
  const params = state.split("-").slice(1).join("-").replaceAll("$", "&");
  if (params) {
    window.location.href = params;
  } else {
    window.history.replaceState({}, document.title);
  }

  // Set user data
  if (tokens_json) {
    setUserData(getUserData(tokens_json));
  }
}

function getRemainingSessionTime(idToken: string) {
  return jwtDecode<idTokenType>(idToken).exp * 1000 - Date.now();
}

export async function refresh(
  setUserData: (data: userDataProps) => void,
  setTokens: (tokens: tokenType) => void,
  apm: ApmBase | undefined
) {
  const cognitoInfos = localStorage.getItem("cognitoInfos");
  const cognitoInfosParsed = cognitoInfos ? JSON.parse(cognitoInfos) : {};
  const old_tokens = localStorage.getItem("tokens");

  if (!old_tokens) return;
  const token_verify = verifyTokens(old_tokens);
  if (token_verify) {
    const res = await fetch(`${cognitoInfosParsed.domain}/oauth2/token`, {
      method: "POST",
      headers: new Headers({
        "content-type": "application/x-www-form-urlencoded",
      }),
      body: Object.entries({
        grant_type: "refresh_token",
        client_id: cognitoInfosParsed.clientId,
        refresh_token: token_verify.refresh_token,
      })
        .map(([k, v]) => `${k}=${v}`)
        .join("&"),
    });
    if (!res.ok) {
      logout();
      return;
    }
    tokens_json = await res.json();
    if (tokens_json === null) return;
    tokens_json.refresh_token = token_verify.refresh_token;
    setTokens(tokens_json);
    if (apm) {
      const decoded_token: any = jwtDecode(tokens_json.access_token);
      apm.addLabels({
        sessionid: decoded_token.jti,
      });
    }
    window.localStorage.setItem("tokens", JSON.stringify(tokens_json));
    setTimeout(
      () => refresh(setUserData, setTokens, apm),
      tokens_json.expires_in * 1000 - 60000
    );
    setUserData(getUserData(tokens_json));
  } else {
    setUserData({} as userDataProps);
  }
}

export async function init(
  setUserData: (data: userDataProps) => void,
  setTokens: (tokens: tokenType) => void,
  apm: ApmBase | undefined
) {
  // Attempt to refresh credentials from active session
  let access_token_diff = 0;

  try {
    const tokens_unverified = window.localStorage.getItem("tokens") || "";
    if (!tokens_unverified) {
      // If no tokens are found, the user is not logged in
      console.log("User is not logged in.");
      return;
    }

    // Verify the tokens
    const tokens_verify = verifyTokens(tokens_unverified);
    if (!tokens_verify) {
      // If tokens cannot be verified, log out the user
      console.log("Tokens cannot be verified. Logging out the user.");
      logout();
      return;
    }

    // Tokens are verified, set them and update the login status
    tokens_json = tokens_verify;
    setTokens(tokens_json);

    // Check the remaining validity period of the access token
    if (tokens_json.access_token) {
      access_token_diff = getRemainingSessionTime(tokens_json.access_token);
    }
  } catch (error) {
    console.error(error);
  }

  // If the access token's validity period is greater than 0, update the login status
  if (access_token_diff > 300000 && tokens_json) {
    // Set user data and start the refresh and timeout timers
    setUserData(getUserData(tokens_json));
    setTimeout(
      () => refresh(setUserData, setTokens, apm),
      access_token_diff - 60000
    );
  } else if (tokens_json && tokens_json.refresh_token) {
    refresh(setUserData, setTokens, apm);
  } else {
    // Otherwise, if the user is not logged in or the access token has expired, log out the user
    logout();
  }
}
